import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FormControl, FormLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextField from '../UI/TextField/TextField';
import Button from '../UI/Buttons/Button';
import AutoCompleteField from '../UI/AutoCompleteField/AutoCompleteField';
import AsyncAutoCompleteField from '../UI/AutoCompleteField/AsyncAutoCompleteField';
import ModalDialog from '../UI/Dialog/ModalDialog';
import { encryptString } from '../../helpers/securityHelper';
import { refreshUnlockChains } from '../../actions/unlockChain';
import common from '../../jsons/common.json';
import ValueHelper from '../../helpers/valueHelper';

// One line describing what a step does to unwind.
export const unwindLabel = (step, t) => {
    if (step.kind === 'reverseTransfer') {
        const legacy = step.snapshot?.legacy && !step.snapshot?.corrected ? ` — ${t('unlockChainLegacy')}` : '';
        return `${t('unlockChainStepTransfer')} ${step.typeLabel} ${step.number} (${step.snapshot?.quantity})${legacy}`;
    }
    if (step.kind === 'releaseReservation') {
        const s = step.snapshot || {};
        return `${t('unlockChainStepRelease')} ${s.containerNumber} — ${s.ownerType || ''} ${s.ownerNumber || ''} (${s.reservation?.quantity})`;
    }
    return `${t('unlockChainStepUnlock')} ${step.typeLabel || ''} ${step.number}`;
};

/**
 * Chain unlock: shows every record, split/combine and reservation that has to be walked back for
 * this record to unlock, takes one approval for all of it, then undoes the steps one at a time -
 * leaves first - ticking each off as the server confirms it. A failure stops the run on that row;
 * everything before it stays undone and the chain can be resumed from here or the redo list.
 *
 * Opened from the Unlock button (objectType + id) or from the redo list to resume (chainId).
 */
function UnlockChainDialog(props) {
    const { show, toggle, objectType, id, chainId, callback } = props;
    const auth = useSelector(state => state.auth);
    const dispatch = useDispatch();
    const { t } = useTranslation('translation');
    const [loading, setLoading] = useState(true);
    const [plan, setPlan] = useState({ steps: [], blockers: [] });
    const [chain, setChain] = useState(null);
    const [running, setRunning] = useState(false);
    const [runningKey, setRunningKey] = useState(null);
    const [failure, setFailure] = useState(null);
    const [reasons, setReasons] = useState([]);
    const [reasonSel, setReasonSel] = useState(null);
    const [comment, setComment] = useState('');
    const [pin, setPin] = useState('');
    const [errors, setErrors] = useState({});
    // Optional NCR / Deviation the chain is run under; written back to on completion.
    const [investigationSel, setInvestigationSel] = useState(null);
    const [investigationInput, setInvestigationInput] = useState('');
    const [investigationSearch, setInvestigationSearch] = useState('');
    const [investigationOptions, setInvestigationOptions] = useState([]);
    const [investigationLoading, setInvestigationLoading] = useState(false);

    useEffect(() => {
        if (!show || chainId != null) return undefined;
        setInvestigationLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await axios.get('/api/unlockchain/investigations', { params: { search: investigationSearch } });
                setInvestigationOptions((res.data || []).map(x => ({ ...x, label: `${x.group} · ${x.label}` })));
            } catch {
                setInvestigationOptions([]);
            } finally {
                setInvestigationLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [show, chainId, investigationSearch]);

    useEffect(() => {
        if (!show) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const cacheValues = await ValueHelper.getPermCachedValues([common.cacheValues.editReason], auth.user.tenant);
                if (!cancelled) setReasons(cacheValues.editReasons || []);
                if (chainId != null) {
                    const res = await axios.get('/api/unlockchain/' + chainId);
                    if (!cancelled) setChain(res.data);
                } else {
                    const res = await axios.get(`/api/unlockchain/plan/${objectType}/${id}`);
                    if (!cancelled) setPlan(res.data);
                }
            } catch (err) {
                if (!cancelled) setFailure({ key: null, message: err.response?.data || err.message });
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [show, chainId, objectType, id, auth.user.tenant]);

    const steps = useMemo(() => {
        const list = chain != null ? chain.steps : plan.steps;
        return [...(list || [])].sort((a, b) => a.seq - b.seq);
    }, [chain, plan]);
    const blockers = chain != null ? [] : (plan.blockers || []);
    const pending = steps.filter(x => x.state == null || x.state === 'pending');
    const finished = chain != null && chain.status !== 'unwinding';

    const run = useCallback(async (current) => {
        setRunning(true);
        setFailure(null);
        let working = current;
        let ran = 0;
        try {
            for (;;) {
                const next = [...working.steps].sort((a, b) => a.seq - b.seq).find(x => x.state === 'pending');
                if (next == null) break;
                setRunningKey(next.key);
                try {
                    const res = await axios.post(`/api/unlockchain/${working._id}/step/${encodeURIComponent(next.key)}/undo`);
                    working = res.data;
                    ran++;
                    setChain(working);
                } catch (err) {
                    setFailure({ key: next.key, message: err.response?.data || err.message });
                    break;
                }
            }
        } finally {
            setRunningKey(null);
            setRunning(false);
            dispatch(refreshUnlockChains());
        }
        // only when this run did the unwinding - a chain handed back already past it is not news
        if (ran > 0 && working.status !== 'unwinding') {
            dispatch({ type: 'CREATE_ALERT', payload: { message: t('unlockChainUnwound'), title: t('success'), severity: 'success' } });
            await Promise.resolve(callback?.());
        }
    }, [dispatch, t, callback]);

    const start = useCallback(async () => {
        if (running) return;
        if (chain != null) {
            // Re-plan from where the chain stands before carrying on: whatever stopped it (or
            // anything consumed since) gets its own steps ahead of the root.
            try {
                setRunning(true);
                const res = await axios.post(`/api/unlockchain/${chain._id}/replan`);
                setChain(res.data);
                await run(res.data);
            } catch (e) {
                setRunning(false);
                const data = e.response?.data;
                if (data?.blockers != null) {
                    setPlan(p => ({ ...p, blockers: data.blockers }));
                    setFailure({ key: null, message: t('unlockChainBlocked') + ' ' + data.blockers.map(b => t(b.key, b.params)).join('; ') });
                } else {
                    setFailure({ key: null, message: data || e.message });
                }
            }
            return;
        }
        const err = {};
        if (reasonSel == null) err.reason = t('required');
        if (comment == null || comment === '') err.comment = t('required');
        if (pin == null || pin === '') err.pin = t('required');
        setErrors(err);
        if (Object.keys(err).length > 0) return;
        try {
            setRunning(true);
            const res = await axios.post('/api/unlockchain', {
                objectType: objectType,
                id: id,
                reason: reasonSel.value,
                comment: comment,
                pin: encryptString(pin),
                investigation: investigationSel != null ? { objectType: investigationSel.objectType, record: investigationSel.value } : null
            });
            setChain(res.data);
            dispatch(refreshUnlockChains());
            await run(res.data);
        } catch (e) {
            setRunning(false);
            const data = e.response?.data;
            if (data?.blockers != null) {
                setPlan(p => ({ ...p, blockers: data.blockers }));
            } else {
                dispatch({ type: 'CREATE_ALERT', payload: { message: data || e.message, title: t('error'), severity: 'error' } });
            }
        }
    }, [running, chain, run, reasonSel, comment, pin, objectType, id, t, dispatch, investigationSel]);

    const stepIcon = (step) => {
        if (step.key === runningKey) return <CircularProgress size={20} />;
        if (failure?.key === step.key || (step.state === 'pending' && step.error && chain != null && !running)) {
            return <ErrorOutlineIcon color="error" />;
        }
        if (step.state === 'undone' || step.state === 'redone') return <CheckCircleIcon color="success" />;
        return <RadioButtonUncheckedIcon color="disabled" />;
    };

    const buttonStack = useMemo(() => {
        const canStart = !loading && !finished && blockers.length === 0 && steps.length > 0 && pending.length > 0;
        return [
            <Button key="close" variant="closeDialog" onClick={toggle}>{t(finished ? 'close' : 'cancel')}</Button>,
            canStart &&
                <Button key="start" variant="save" onClick={start} disabled={running}>
                    {t(chain != null ? 'unlockChainResume' : 'unlockChainStart')}
                </Button>
        ];
    }, [loading, finished, blockers.length, steps.length, pending.length, toggle, t, start, running, chain]);

    return (
        <ModalDialog buttonStack={buttonStack} toggleDialog={toggle} dialogStatus={show === true} fullWidth maxWidth="md"
            title={t('unlockChain') + (chain != null ? ' ' + chain.number : '')}>
            {loading ?
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2">{t('unlockChainPlanning')}</Typography>
                </Box> :
                <>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {finished ? t('unlockChainUnwound') : t('unlockChainIntro')}
                    </Typography>
                    {blockers.length > 0 &&
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {t('unlockChainBlocked')}
                            <ul style={{ margin: '4px 0 0', paddingInlineStart: '1.2em' }}>
                                {blockers.map((b, i) => <li key={i}>{t(b.key, b.params)}</li>)}
                            </ul>
                        </Alert>
                    }
                    {failure != null &&
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {failure.message}{failure.key != null ? ' — ' + t('unlockChainStopped') : ''}
                        </Alert>
                    }
                    {steps.length === 0 && blockers.length === 0 && failure == null &&
                        <Typography variant="body2">{t('unlockChainNoSteps')}</Typography>
                    }
                    <List dense sx={{ maxHeight: 420, overflow: 'auto' }}>
                        {steps.map(step => (
                            <ListItem key={step.key} data-cy={'chainStep-' + step.seq}>
                                <ListItemIcon sx={{ minWidth: 36 }}>{stepIcon(step)}</ListItemIcon>
                                <ListItemText
                                    primary={`${step.seq}. ${unwindLabel(step, t)}`}
                                    secondary={failure?.key === step.key ? failure.message :
                                        step.kind === 'unlock' ? step.originalStatusLabel : null}
                                    secondaryTypographyProps={failure?.key === step.key ? { color: 'error' } : undefined}
                                />
                            </ListItem>
                        ))}
                    </List>
                    {chain?.investigation?.number != null &&
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {t('unlockChainInvestigation')}: {chain.investigation.number}{chain.investigation.title ? ' - ' + chain.investigation.title : ''}
                        </Typography>
                    }
                    {chain == null && blockers.length === 0 && steps.length > 0 &&
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={12}>
                                <AsyncAutoCompleteField
                                    value={investigationSel}
                                    inputValue={investigationInput}
                                    options={investigationOptions}
                                    loading={investigationLoading}
                                    onChange={(e, v) => setInvestigationSel(v)}
                                    onInputChange={(e, v, reason) => {
                                        setInvestigationInput(v);
                                        // selecting fills the input with the label; only typing searches
                                        if (reason === 'input' || reason === 'clear') setInvestigationSearch(reason === 'clear' ? '' : v);
                                    }}
                                    label={t('unlockChainInvestigation')}
                                    helperText={t('unlockChainInvestigationHelp')}
                                    noOptionsText={t('noRecords')}
                                />
                            </Grid>
                            <Grid size={10}>
                                <AutoCompleteField value={reasonSel} options={reasons} onChange={(e, v) => setReasonSel(v)}
                                    error={errors.reason != null} helperText={errors.reason} label={t('reason')} />
                            </Grid>
                            <Grid size={12}>
                                <TextField value={comment} onChange={e => setComment(e.target.value)} name="comment" multiline={true} rows="3"
                                    variant="outlined" size="medium" fullWidth label={t('comment')}
                                    error={errors.comment != null} helperText={errors.comment} />
                            </Grid>
                            <Grid size={8}>
                                <FormControl>
                                    <FormLabel>{t('user')}</FormLabel>
                                    {auth.user.firstName + ' ' + auth.user.lastName}
                                </FormControl>
                            </Grid>
                            <Grid size={4}>
                                <TextField value={pin} onChange={e => setPin(e.target.value)} name="pin"
                                    size="medium" fullWidth label={t('pin')} type={'password'}
                                    error={errors.pin != null} helperText={errors.pin} />
                            </Grid>
                            <Grid size={12}>
                                <Typography variant="body2" color="textSecondary">{t('unlockConsent')}</Typography>
                            </Grid>
                        </Grid>
                    }
                </>
            }
        </ModalDialog>
    );
}

export default UnlockChainDialog;
